import ApiService from "@/core/services/ApiService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

// 卡池项目接口
export interface CardPoolItem {
  id: number;
  card_pool_id: number;
  card_id: number;
  card?: {
    id: number;
    name: string;
    image: string | null;
    rarity: string;
    rarity_display?: string;
    card_language_id?: number;
    card_language_name?: string;
    card_version_id?: number;
    card_version_name?: string;
  };
  weight: number;
  guaranteed_weight: number | null; // 保底权重（能量满时使用）
  level: string;           // 卡池内等级（必填，用于保底判断）
  effective_level: string; // 有效等级（目前等同于 level）
  sort: number;
  is_animation: boolean;
  created_at: string;
  updated_at: string;
}

// 卡池接口
export interface CardPool {
  id: number;
  card_series_id: number;
  card_series?: {
    id: number;
    name: string;
  };
  name: string;
  description: string | null;
  price: number;
  energy_limit: number;
  guaranteed_rarities?: string[];
  energy_trigger_rarities?: string[];
  image: string | null;
  enabled: boolean;
  sort: number;
  items_count?: number;
  draw_records_count?: number;
  card_pool_items?: CardPoolItem[];
  is_presale: boolean;
  is_arrived: boolean;
  arrived_at: string | null;
  created_at: string;
  updated_at: string;
  updated_by?: string;
}

// 下拉选项接口
export interface CardPoolOption {
  id: number;
  name: string;
}

// 模拟结果接口
export interface CardPoolSimulationResult {
  simulation_count: number;
  price: number;
  cards_per_pack: number;
  energy_limit: number;
  avg_reward_value: number;
  expected_profit: number;
  profit_rate: number;
  guaranteed_trigger_rate: number;
  guaranteed_trigger_count: number;
  total_cards_drawn: number;
  rarity_distribution: Array<{
    rarity: string;
    count: number;
    percentage: number;
    avg_recycle_price: number;
  }>;
  energy_distribution: Array<{
    energy: number;
    count: number;
    percentage: number;
  }>;
}

// 概率预览接口
export interface ProbabilityItem {
  card_id: number;
  card_name: string;
  rarity: string;
  rarity_display?: string;
  weight: number;
  probability: number;
  probability_display: string;
}

@Module
export default class CardPoolModule extends VuexModule {
  card_pools_list = [] as Array<CardPool>;
  card_pools_count = 0;
  card_pool = {} as CardPool;
  all_card_pools = [] as Array<CardPoolOption>;
  card_pool_items = [] as Array<CardPoolItem>;
  card_pool_probability = [] as Array<ProbabilityItem>;

  get currentCardPoolsList(): Array<CardPool> {
    return this.card_pools_list;
  }

  get currentCardPool(): CardPool {
    return this.card_pool;
  }

  get currentCardPoolsCount(): number {
    return this.card_pools_count;
  }

  get currentAllCardPools(): Array<CardPoolOption> {
    return this.all_card_pools;
  }

  get currentCardPoolItems(): Array<CardPoolItem> {
    return this.card_pool_items;
  }

  get currentCardPoolProbability(): Array<ProbabilityItem> {
    return this.card_pool_probability;
  }

  @Mutation
  [Mutations.SET_CARD_POOLS_LIST](card_pools_list: Array<CardPool>) {
    this.card_pools_list = card_pools_list;
  }

  @Mutation
  [Mutations.SET_CARD_POOL](card_pool: CardPool) {
    this.card_pool = card_pool;
  }

  @Mutation
  [Mutations.SET_CARD_POOLS_COUNT](count: number) {
    this.card_pools_count = count;
  }

  @Mutation
  [Mutations.SET_ALL_CARD_POOLS](all_card_pools: Array<CardPoolOption>) {
    this.all_card_pools = all_card_pools;
  }

  @Mutation
  [Mutations.SET_CARD_POOL_ITEMS](items: Array<CardPoolItem>) {
    this.card_pool_items = items;
  }

  @Mutation
  [Mutations.SET_CARD_POOL_PROBABILITY](probability: Array<ProbabilityItem>) {
    this.card_pool_probability = probability;
  }

  @Action
  [Actions.GET_CARD_POOLS](params: Record<string, unknown>) {
    ApiService.setHeader();

    let queryString = "?";
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryString += "&" + key + "=" + params[key];
        }
      });
    }

    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/card_pools", queryString)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_CARD_POOLS_LIST, data["card_pools"] || []);
          this.context.commit(Mutations.SET_CARD_POOLS_COUNT, data["total_result"] || 0);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.GET_CARD_POOL](id: number) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.get(`admin/card_pools/${id}`)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_CARD_POOL, data["card_pool"]);
          if (data["card_pool"]?.card_pool_items) {
            this.context.commit(Mutations.SET_CARD_POOL_ITEMS, data["card_pool"].card_pool_items);
          }
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.CREATE_CARD_POOL](params: Record<string, unknown>) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string; card_pool?: CardPool }>((resolve, reject) => {
      ApiService.post("admin/card_pools", { card_pool: params })
        .then(({ data }) => {
          if (data.success) {
            this.context.commit(Mutations.SET_CARD_POOL, data["card_pool"]);
          }
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.UPDATE_CARD_POOL](credentials: { id: number; values: Record<string, unknown> }) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string }>((resolve, reject) => {
      ApiService.put(`admin/card_pools/${credentials.id}`, { card_pool: credentials.values })
        .then(({ data }) => {
          if (data.success) {
            this.context.commit(Mutations.SET_CARD_POOL, data["card_pool"]);
          }
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.DELETE_CARD_POOL](id: number) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string }>((resolve, reject) => {
      ApiService.delete(`admin/card_pools/${id}`)
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.GET_ALL_CARD_POOLS](params?: { card_series_id?: number }) {
    ApiService.setHeader();

    let queryString = "";
    if (params?.card_series_id) {
      queryString = `?card_series_id=${params.card_series_id}`;
    }

    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/card_pools/all", queryString)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_ALL_CARD_POOLS, data["card_pools"] || []);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.GET_CARD_POOL_PROBABILITY](id: number) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.get(`admin/card_pools/${id}/probability_info`)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_CARD_POOL_PROBABILITY, data["probability_info"] || []);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.SIMULATE_CARD_POOL](params: { id: number; times?: number }) {
    ApiService.setHeader();

    return new Promise<CardPoolSimulationResult>((resolve, reject) => {
      ApiService.post(`admin/card_pools/${params.id}/simulate`, {
        times: params.times || 10000
      })
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '模拟失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.DUPLICATE_CARD_POOL](id: number) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string; card_pool?: CardPool }>((resolve, reject) => {
      ApiService.post(`admin/card_pools/${id}/duplicate`, {})
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '复制失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.ARRIVE_CARD_POOL](id: number) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string }>((resolve, reject) => {
      ApiService.post(`admin/card_pools/${id}/arrive`, {})
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '到货操作失败');
          reject(response?.data);
        });
    });
  }

  // 卡池项目相关 Actions
  @Action
  [Actions.GET_CARD_POOL_ITEMS](card_pool_id: number) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.get(`admin/card_pools/${card_pool_id}/card_pool_items`)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_CARD_POOL_ITEMS, data["card_pool_items"] || []);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.CREATE_CARD_POOL_ITEM](params: { card_pool_id: number; item: Record<string, unknown> }) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string }>((resolve, reject) => {
      ApiService.post(`admin/card_pools/${params.card_pool_id}/card_pool_items`, { card_pool_item: params.item })
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.BATCH_CREATE_CARD_POOL_ITEMS](params: { card_pool_id: number; items: Record<string, unknown>[] }) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string; created_count?: number }>((resolve, reject) => {
      ApiService.post(`admin/card_pools/${params.card_pool_id}/card_pool_items/batch_create`, { items: params.items })
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.UPDATE_CARD_POOL_ITEM](params: { card_pool_id: number; item_id: number; values: Record<string, unknown> }) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string }>((resolve, reject) => {
      ApiService.put(`admin/card_pools/${params.card_pool_id}/card_pool_items/${params.item_id}`, { card_pool_item: params.values })
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.DELETE_CARD_POOL_ITEM](params: { card_pool_id: number; item_id: number }) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string }>((resolve, reject) => {
      ApiService.delete(`admin/card_pools/${params.card_pool_id}/card_pool_items/${params.item_id}`)
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject(response?.data);
        });
    });
  }

  @Action
  [Actions.BATCH_UPDATE_CARD_POOL_ITEMS](params: { card_pool_id: number; items: Array<Record<string, unknown>> }) {
    ApiService.setHeader();

    return new Promise<{ success: boolean; error_msg?: string }>((resolve, reject) => {
      ApiService.put(`admin/card_pools/${params.card_pool_id}/card_pool_items/batch_update`, { items: params.items })
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject(response?.data);
        });
    });
  }
}
